import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import axios from 'axios';

const Subscription = () => {
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      console.log('checkSubscriptionStatus. isAuthenticated: ', isAuthenticated);
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      
      try {
        const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
        const response = await axios.get(`${baseUrl}/api/stripe/subscription-status`, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
        setHasSubscription(response.data.hasSubscription);
        
        // Update localStorage
        if (response.data.hasSubscription) {
          localStorage.setItem('hasSubscription', 'true');
        } else {
          localStorage.removeItem('hasSubscription');
        }
      } catch (err) {
        console.error('Error checking subscription status:', err);
        if(err.response.status === 401) {
          navigate('/login');
        }
      }
    };
    
    checkSubscriptionStatus();
  }, [isAuthenticated]);

  const handleSubscribe = async () => {
    console.log('handleSubscribe');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.post(`${baseUrl}/api/stripe/create-checkout-session`,
        {},
        {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to start subscription process. Please try again.');
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    console.log('handleManageSubscription');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.post(`${baseUrl}/api/stripe/create-customer-portal-session`,
        {},
        {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to start subscription process. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Unlock Your Musical Journey
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Enjoy unlimited access to our exclusive library of unique AI-generated music and create up to 100 personalized songs monthly with our premium subscription.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h2" gutterBottom>
              Premium Subscription
            </Typography>
            <Typography variant="h3" component="div" sx={{ mb: 2 }}>
              $10
              <Typography variant="subtitle1" component="span" color="text.secondary">
                /month
              </Typography>
            </Typography>
          </Box>

          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Unlimited Access to Music Catalog"
                secondary="Enjoy unlimited access to all songs in our exclusive AI-generated library"
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <MusicNoteIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Custom Playlists & Songs"
                secondary="Generate playlists and up to 100 royalty-free songs per month in any style"
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <AutoAwesomeIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Premium Features"
                secondary="Access to all premium features and future updates"
              />
            </ListItem>
          </List>

          <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {hasSubscription ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  You are currently subscribed to Melo Premium!
                </Alert>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleManageSubscription}
                  startIcon={<ManageAccountsIcon />}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '20px',
                    padding: '12px 24px',
                    '&:hover': {
                      borderColor: '#3AC9A7',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Manage Subscription'}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={handleSubscribe}
                disabled={loading}
                sx={{
                  color: 'black',
                  textTransform: 'none',
                  borderRadius: '20px',
                  padding: '12px 24px',
                  '&:hover': {
                    backgroundColor: '#3AC9A7',
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} /> : (isAuthenticated ? 'Subscribe Now' : 'Sign in to Subscribe')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Subscription; 